import Box from '@mui/material/Box';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';
import type { SxProps, Theme } from '@mui/material/styles';
import React, { useRef } from 'react';

/** Displays an react component as popper description of another react component (children) */
function DescriptionPopper({
  children,
  description,
  placement = 'top',
  width,
  sx,
  offset = [0, 8],
}: {
  children?: React.ReactNode;
  description: React.ReactNode | string;
  placement?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'left-start'
    | 'left-end'
    | 'right-start'
    | 'right-end';
  width?: string | number;
  sx?: SxProps<Theme>;
  offset?: [number, number];
}) {
  // Refs and others control values and status
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;

  // function that controls popper reference when mouse enter on child component
  const handleMouseEnter = () => {
    setAnchorEl(popperRef.current);
  };

  // function that controls popper reference when mouse leaves on child component
  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      aria-describedby={id}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ display: 'flex', width: width || 'auto', ...sx }}
      ref={popperRef}
    >
      {children}
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset,
            },
          },
        ]}
        sx={{ zIndex: 1500 }}
      >
        <Grow in={open} {...(open ? { timeout: 200 } : {})}>
          <Box>{description}</Box>
        </Grow>
      </Popper>
    </Box>
  );
}

export default DescriptionPopper;
